import './style.css';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { createModal, LoadingIndicator, Tooltip } from '@thermon-platforms/thermon-ui-core';
import { useLocation } from 'react-router-dom';
import FocusModal from './focus-modal';
import PowerModal from './power-modal';
import EditModal from './edit-modal';
import HistoricalChart from './components/historical-chart';
import Gauge from 'components/circuit-gauges';
import useAlarmsStore, { getMaxSeverity } from 'store/alarms';
import useDevicesStore from 'store/devices';
import useDeviceTypesStore from 'store/device-types';
import useUsersStore from 'store/users';
import useRolesStore from 'store/roles';
import StatusTabs from './status-tabs';
import ImagesButton from '../images';
import { getConfigItem } from 'util/device';
import StatusIcon from 'components/status-icon';
import LockOutButton from 'components/lockout-button';
import { NotesModal } from 'components/notes';
import { last } from 'lodash';


function CircuitView({device}) {
  const { hash } = useLocation();
  const [alarmsStore] = useAlarmsStore();
  const [, devicesStoreActions] = useDevicesStore();
  const [deviceTypesStore] = useDeviceTypesStore();
  const [, usersStoreActions] = useUsersStore();
  const [rolesStore] = useRolesStore();
  const [detail, setDetail] = useState();
  const [status, setStatus] = useState();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [alarms, setAlarms] = useState([]);
  const focusInterval = useRef();
  const [focusMode, setFocusMode] = useState(false);
  const [renderChart, setRenderChart] = useState(false);
  const parent = last(device.path);
  const index = parent.children.indexOf(device.model);
  const prev = parent.children[index - 1] || last(parent.children);
  const next = parent.children[index + 1] || parent.children[0];
  const controller = deviceTypesStore.types.find(type => type.id == parent.deviceModel);
  const supportsPowerState = controller?.configuration?.find(item => item.section == 'Circuit')?.items?.find(item => item.key == 'PowerState')?.options?.length > 0;
  const canEdit = usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles);

  function fetchStatus() {
    devicesStoreActions.fetchStatus(device.model.id).then((status) => {
      setStatus(status);
    });
  }

  function refreshStatus() {
    setRefreshing(true);
    devicesStoreActions.refresh(device.model.id)
      .then(() => {
        fetchStatus();
      })
      .finally(() => {
        setTimeout(() => {
          setRefreshing(false);
        }, 300);
      });
  }

  // NOTE - this should be updated to use whatever mechanism
  // requests fresh readings from the network
  function enableFocusMode() {
    if (!focusMode) {
      const focusModal = createModal(<FocusModal device={device} />);
      focusModal.open()
        .then(interval => {
          // Immediately request an update
          refreshStatus();
          // Set an interval based on the user configured time
          focusInterval.current = setInterval(refreshStatus, interval * 1000);
          // Update our state
          setFocusMode(true);
        })
        .catch(() => {/* no-op */})
    } else {
      if (focusInterval.current) clearInterval(focusInterval.current);
      setFocusMode(false);
    }
  }
  // Clear our focus interval if the user navigates away
  useEffect(() => {
    return () => {
      if (focusInterval.current) clearInterval(focusInterval.current);
    }
  }, []);

  const statusInterval = useRef();
  useEffect(() => {
    setLoading(true);

    devicesStoreActions.fetchDetail(device.model.id).then((detail) => {
      setDetail(detail);
      setLoading(false);
      setTimeout(() => {
        setRenderChart(true);
      }, 100);
    });

    fetchStatus();
    statusInterval.current = setInterval(fetchStatus, 60 * 1000);

    return () => {
      if (statusInterval.current) clearInterval(statusInterval.current);
    }
  }, [device]);

  useEffect(() => {
    if (!alarmsStore.alarms.length) {
      return;
    }
    setAlarms(alarmsStore.alarms.filter(alarm => alarm.deviceId == device.model.id));
  }, [alarmsStore]);

  function editSettings() {
    setSettingsLoading(true);
    devicesStoreActions.fetchDetail(device.model.id).then((res) => {
      setSettingsLoading(false);
      setDetail(res);
      const editModal = createModal(
        <EditModal
          device={res}
          parent={parent}
          controller={controller}
          temperatureUnit={status?.temperatureUnit}
        />
      );
      editModal.open()
        .then(() => {
          // Handle side-effects
          devicesStoreActions.forceUpdate();
        })
        .catch(() => {/* no-op */});
    });
  }

  function editPowerMode() {
    setSettingsLoading(true);
    devicesStoreActions.fetchDetail(device.model.id).then((res) => {
      setSettingsLoading(false);
      setDetail(res);
      const powerModal = createModal(<PowerModal device={res} controller={controller} />);
      powerModal.open()
        .then((res) => {
          refreshStatus();
          devicesStoreActions.fetchStatus(device.model.id).then((res) => {
            setStatus(res);
          });
        })
        .catch(() => {/* no-op */});
    });
  }

  function getLatestReadingTime() {
    if (!status) return t`Unknown`;
    const timestamps = [
      status.currentOccurredAt,
      status.temperatureOccurredAt,
      status.groundCurrentOccurredAt,
      status.powerDeliveryOccurredAt,
    ];
    const latest = timestamps
      .filter(ts => ts)
      .sort((a, b) => new Date(a) - new Date(b))
      .pop();
    if (latest) return new Date(latest).toLocaleString();
    return t`Unknown`;
  }

  function viewNotes() {
    const notesModal = createModal(<NotesModal device={device?.model} />);
    notesModal.open()
      .then(() => {
        // Handle side-effects
      })
      .catch(() => {/* no-op */});
  }

  return (<>
    <header>
      <h2>
        {loading ?
          <LoadingIndicator /> :
          <StatusIcon device={device} alarms={alarms} />
        }
        {device.model.name}
      </h2>
      <ul className="device-controls">
        {canEdit ? <>
          <li>
            <button
              className={`settings ${settingsLoading ? 'processing' : ''}`}
              onClick={editSettings}
              data-tooltip-content={t`Circuit Settings`}
              data-tooltip-id="control-tooltip"
              disabled={loading || settingsLoading}
            >
              <Trans>Settings</Trans>
            </button>
          </li>
        </> : <></>}
        <li>
          <button
            className={`refresh ${refreshing ? 'processing' : ''}`}
            data-tooltip-content={t`Refresh Settings and Readings`}
            data-tooltip-id="control-tooltip"
            disabled={loading || refreshing}
            onClick={refreshStatus}
          >
            <Trans>Refresh Settings and Readings</Trans>
          </button>
        </li>
        <li>
          <button
            className={`focus-mode ${focusMode ? 'active' : ''}`}
            data-tooltip-content={!focusMode ? t`Enable Focus Mode` : t`Disable Focus Mode`}
            data-tooltip-id="control-tooltip"
            disabled={loading}
            onClick={enableFocusMode}
          >
            {!focusMode ?
              <Trans>Enable Focus Mode</Trans> :
              <Trans>Disable Focus Mode</Trans>
            }
          </button>
        </li>
        {canEdit ? <li>
          <LockOutButton device={device} disabled={loading} />
        </li> : null}
        {canEdit && supportsPowerState ? <li>
          <button
            className="power"
            data-tooltip-content={t`Select Power Mode`}
            data-tooltip-id="control-tooltip"
            onClick={editPowerMode}
            disabled={!detail}
          >
            <Trans>Power Mode</Trans>
          </button>
        </li> : <></>}
        <li>
          <button
            className="note"
            data-tooltip-content={t`View Notes`}
            data-tooltip-id="control-tooltip"
            onClick={viewNotes}
          >
            <Trans>Notes</Trans>
          </button>
        </li>
        <li>
          <ImagesButton device={device} />
        </li>
        {parent.children.length > 1 ? <li>
          <Link to={`../${prev.id}${hash || ''}`} relative="path" className="button prev">Previous</Link>
        </li> : null}
        {parent.children.length > 1 ? <li>
          <Link to={`../${next.id}${hash || ''}`} relative="path" className="button next">Next</Link>
        </li> : null}
      </ul>
      <Tooltip id="control-tooltip" place="bottom-end" />
    </header>
    <section>
      {detail && status ? <div className="circuit-status">
        <div className="overview">
          <ul>
            <li>
              <strong><Trans>Status</Trans></strong>: {status?.commStatus != 'ok' ? status?.commStatus : getMaxSeverity(alarms)}
            </li>
            <li>
              {/* TODO - grab all the timestamps and find the most recent one */}
              <strong><Trans>Latest Reading</Trans></strong>: {getLatestReadingTime()}
            </li>
            {/* <li>
              <strong><Trans>Circuit Number</Trans></strong>: {detail?.circuitNumberDisplay}
            </li> */}
            {supportsPowerState ? <li>
              <strong><Trans>Power State</Trans></strong>: {status?.powerStateDisplayName || status?.powerState || t`Unknown`}
            </li> : null}
            <li>
              <strong><Trans>Process Tag</Trans></strong>:&nbsp;
              {getConfigItem(detail, 'ProcessTag')?.value ? (
                <Link to={`/devices/tag-${encodeURI(getConfigItem(detail, 'ProcessTag')?.value)}`}>
                  {getConfigItem(detail, 'ProcessTag')?.value}
                </Link>
              ) : t`None`}
            </li>
            {/* {device?.model.tags ?
              <li>
                <strong><Trans>Tags</Trans></strong>:&nbsp;
                {device?.model?.tags?.length ? device.model.tags.map(tag =>
                  <span key={`tag-${tag}`}>
                    <Link to={`/devices/tag-${encodeURI(tag)}`}>
                      {tag}
                    </Link>&nbsp;
                  </span>
                ) : 'None'}
              </li>
            : <></>} */}
          </ul>
        </div>
        <div className="circuit-gauge-set">
          {/* Don't show current gauge for steam controllers */}
          {controller?.id != 28 && controller?.id != 34 ? <Gauge
            size={120}
            model={
              {
                low: +getConfigItem(detail, 'CurrentLowAlarm')?.value,
                high: +getConfigItem(detail, 'CurrentHighAlarm')?.value,
                trip: +getConfigItem(detail, 'CurrentTripAlarm')?.value,
                current: status.commStatus != 'ok' ? null : status.current
              }
            }
            unit={'A'}
            className="current"
          /> : null}
          <Gauge
            model={
              {
                low: +getConfigItem(detail, 'TemperatureLowAlarm')?.value,
                high: +getConfigItem(detail, 'TemperatureHighAlarm')?.value,
                trip: +getConfigItem(detail, 'TemperatureHighTrip')?.value,
                maintain: +getConfigItem(detail, 'TemperatureMaintain')?.value,
                max: +getConfigItem(detail, 'TemperatureMaximum')?.value,
current: status.commStatus != 'ok' ? null : status.temperature,
               power: status.commStatus != 'ok' ? null : status.powerDelivery
              }
            }
            unit={`°${status.temperatureUnit || '–'}`}
            className="temperature"
          />
          {/* Don't show current gauge for steam controllers */}
          {controller?.id != 28 && controller?.id != 34 ? <Gauge
            size={120}
            model={
              {
                low: +getConfigItem(detail, 'GroundCurrentLowAlarm')?.value,
                high: +getConfigItem(detail, 'GroundCurrentHighAlarm')?.value,
                trip: +getConfigItem(detail, 'GroundCurrentTripAlarm')?.value,
                current: status.commStatus != 'ok' ? null : status.groundCurrent
              }
            }
            unit={'mA'}
            className="ground-current"
          /> : null}
        </div>
        <StatusTabs alarms={alarms} device={detail} status={status} />
      </div> : null}
      {/* <h3>Temperature and Current</h3> */}
      {!loading && renderChart ? <HistoricalChart
        showCurrent={controller?.id != 28 && controller?.id != 34}
        circuitIds={[device.model.id]}
        circuitName={device.model.name}
        deviceIndices={detail?.deviceIndices}
        alarmBands={{
          temperature: {
            low: +getConfigItem(detail, 'TemperatureLowAlarm')?.value,
            high: +getConfigItem(detail, 'TemperatureHighAlarm')?.value,
            trip: +getConfigItem(detail, 'TemperatureHighTrip')?.value,
          },
          current: {
            low: +getConfigItem(detail, 'CurrentLowAlarm')?.value,
            high: +getConfigItem(detail, 'CurrentHighAlarm')?.value,
            trip: +getConfigItem(detail, 'CurrentHighTrip')?.value,
          }
        }}
      /> : <></>}
    </section>
  </>);
}

export default CircuitView;
